import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Download, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const [status, setStatus] = useState("polling");
  const [record, setRecord] = useState(null);
  const [download, setDownload] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sid) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        setRecord(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          try {
            const dl = await api.get(`/payments/download/${sid}`);
            setDownload(dl.data);
          } catch {}
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
        if (attempts >= 20) { setStatus("timeout"); return; }
        setAttempts((n) => n + 1);
        setTimeout(poll, 2000);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sid]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[--sanctum-bg]">
      <div className="max-w-lg w-full text-center">
        <Link to="/" className="font-display text-3xl inline-block mb-10">Sanctum</Link>
        {status === "polling" && (
          <>
            <div className="w-14 h-14 rounded-full border-2 border-neutral-200 border-t-neutral-900 animate-spin mx-auto mb-6" />
            <h1 className="font-display text-3xl">Confirming your payment…</h1>
            <p className="text-neutral-500 mt-3 text-sm">This usually takes a couple of seconds.</p>
          </>
        )}
        {status === "paid" && (
          <div data-testid="payment-success-panel">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-6" strokeWidth={1.2} />
            <h1 className="font-display text-4xl">Thank you.</h1>
            <p className="text-neutral-500 mt-3">
              Your {record?.format?.toUpperCase()} of <em>{record?.title}</em> by {record?.artist} is ready.
            </p>
            {download && (
              <a
                href={download.download_url}
                target="_blank" rel="noreferrer"
                data-testid="download-link"
                className="btn-pill btn-primary mt-8 inline-flex"
                download
              >
                <Download size={14} strokeWidth={1.5} /> Download {download.format.toUpperCase()}
              </a>
            )}
            <div className="mt-8">
              <Link to="/songs" className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
                Keep browsing <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        )}
        {(status === "failed" || status === "timeout") && (
          <div>
            <h1 className="font-display text-3xl">We couldn't confirm the payment.</h1>
            <p className="text-neutral-500 mt-3 text-sm">If money was taken, contact us and we'll sort it immediately.</p>
            <Link to="/store" className="btn-pill btn-outline mt-8 inline-flex">Back to store</Link>
          </div>
        )}
      </div>
    </div>
  );
}
